import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import api, { extractApiError } from "@/lib/api";

const CATEGORIES = [
  { value: "account", label: "Account / login" },
  { value: "order", label: "Order issue" },
  { value: "payout", label: "Payouts / deposits" },
  { value: "report", label: "Report a user" },
  { value: "feature", label: "Feature request" },
  { value: "other", label: "Other" },
];

/**
 * ContactSupportDialog — single button that opens a support ticket.
 * Submits to POST /api/support/tickets which routes to ALL admins.
 */
export default function ContactSupportDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "other", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", {
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      toast.success("Support ticket sent. We typically reply within a few hours.");
      setForm({ category: "other", subject: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(extractApiError(err, "Could not send ticket."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          data-testid="support-open"
          variant="outline"
          className="border-emerald-700/40 text-emerald-800 hover:bg-emerald-50"
        >
          <LifeBuoy size={14} className="mr-1.5" /> Contact Support
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-stone-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">Contact Support</DialogTitle>
          <DialogDescription className="text-stone-600 font-sans text-sm">
            Send a message to the Azure Gypsy Farms admin team. We typically reply within a few hours.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm font-sans">
          <div>
            <Label className="text-xs font-semibold text-stone-800">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="support-category" className="mt-2 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="support-subject" className="text-xs font-semibold text-stone-800">Subject</Label>
            <Input
              id="support-subject"
              data-testid="support-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Briefly, what's it about?"
              className="mt-2 bg-white"
            />
          </div>
          <div>
            <Label htmlFor="support-message" className="text-xs font-semibold text-stone-800">Message</Label>
            <Textarea
              id="support-message"
              data-testid="support-message"
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us what happened. Include order IDs or dates if relevant."
              className="mt-2 bg-white"
            />
          </div>
          <p className="text-xs text-stone-500">
            Your message goes to every admin and shows up in their back-office Support tab. You'll get a phone alert when we reply.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="support-submit"
            className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"
          >
            {submitting ? "Sending…" : "Send to Support"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
